import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../stores/store';
import { decrementUnreadCount } from '../../../stores/slices/notification-slice';
import CustomerPageLayout from '../customer-page-layout/CustomerPageLayout';
import { useLazyGetMyNotificationsQuery, useLazyGetNotificationByIdQuery } from '../../../service/notification-service';
import { UserNotification, NotificationFilter, NotificationType } from '../../../model/notifications/notifications';
import { TransactionCategory } from '../../../model/transaction';
import NotificationItem from './components/NotificationItem';
import { BounceLoader } from 'react-spinners';
import styles from './NotificationPage.module.scss';
import { ApplicationRoutes } from '../../../routes';
import RefundTransactionModal from '../../../common/components/refund-transaction-modal/RefundTransactionModal';

const PAGE_SIZE = 7;

const NotificationPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState<NotificationFilter>(NotificationFilter.All);
    const [allNotifications, setAllNotifications] = useState<UserNotification[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [refundReference, setRefundReference] = useState<string | null>(null);

    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadingRef = useRef(false);

    const [fetchNotifications, { isFetching, error }] = useLazyGetMyNotificationsQuery();
    const [fetchNotificationById] = useLazyGetNotificationByIdQuery();

    useEffect(() => {
        loadingRef.current = isFetching;
    }, [isFetching]);

    useEffect(() => {
        let cancelled = false;
        const isFirstPage = currentPage === 1;

        fetchNotifications({ page: currentPage, pageSize: PAGE_SIZE, notificationFilter: activeFilter })
            .then(result => {
                if (cancelled || !result.data) return;
                if (isFirstPage) {
                    setAllNotifications(result.data.items);
                } else {
                    setAllNotifications(prev => [...prev, ...result.data!.items]);
                }
                setHasMore(result.data.hasNextPage);
            });

        return () => { cancelled = true; };
    }, [activeFilter, currentPage, fetchNotifications]);

    const lastItemRef = useCallback((node: HTMLDivElement | null) => {
        if (observerRef.current) observerRef.current.disconnect();
        if (!hasMore) return;

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !loadingRef.current) {
                loadingRef.current = true;
                setCurrentPage(p => p + 1);
            }
        }, { rootMargin: '100px', threshold: 0.1 });

        if (node) observerRef.current.observe(node);
    }, [hasMore]);

    const handleTabChange = (filter: NotificationFilter) => {
        setAllNotifications([]);
        setCurrentPage(1);
        setActiveFilter(filter);
    };

    const handleSelect = (notification: UserNotification) => {
        if (!notification.readAt) {
            dispatch(decrementUnreadCount());
            setAllNotifications(prev =>
                prev.map(n => n.id === notification.id ? { ...n, readAt: new Date().toISOString() } : n)
            );
        }

        if (notification.type === NotificationType.BorrowToken) {
            fetchNotificationById({ id: notification.id }).then(result => {
                const raw = result.data?.notificationContext as any;
                const ctx = typeof raw === 'string' ? JSON.parse(raw) : raw;
                const ref = ctx?.transactionReference ?? ctx?.TransactionReference;
                if (ref) {
                    navigate(`/${ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY}/${ref}`, {
                        state: { category: TransactionCategory.VendingTransaction },
                    });
                } else {
                    navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}/${notification.id}`);
                }
            });
            return;
        }

        if (notification.type === NotificationType.Refund) {
            fetchNotificationById({ id: notification.id }).then(result => {
                const raw = result.data?.notificationContext as any;
                const ctx = typeof raw === 'string' ? JSON.parse(raw) : raw;
                // Original transaction's reference; the refund modal looks the refund up by it
                const ref = ctx?.transactionReference ?? ctx?.TransactionReference;
                if (ref) {
                    setRefundReference(ref);
                } else {
                    navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}/${notification.id}`);
                }
            });
            return;
        }

        if (notification.type === NotificationType.ServiceRequest) {
            fetchNotificationById({ id: notification.id }).then(result => {
                const raw = result.data?.notificationContext as any;
                const ctx = typeof raw === 'string' ? JSON.parse(raw) : raw;
                const instanceId = ctx?.serviceRequestInstanceId ?? ctx?.ServiceRequestInstanceId;
                if (instanceId != null) {
                    navigate(`/${ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_DETAIL.replace(":id", String(instanceId))}`);
                } else {
                    navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}/${notification.id}`);
                }
            });
            return;
        }

        navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}/${notification.id}`);
    };

    const isInitialLoad = isFetching && allNotifications.length === 0;
    const isLoadingMore = isFetching && allNotifications.length > 0;

    const renderListContent = () => {
        if (error) {
            return (
                <div className={styles.centered}>
                    <p className={styles.error}>Unable to load notifications. Please try again.</p>
                    <button className={styles.retryBtn} onClick={() => fetchNotifications({ page: currentPage, pageSize: PAGE_SIZE, notificationFilter: activeFilter })}>
                        Retry
                    </button>
                </div>
            );
        }
        return (
            <div className={styles.notificationsCard}>
                {allNotifications.map((notification, index) => {
                    const isLast = index === allNotifications.length - 1;
                    return (
                        <div key={notification.id} ref={isLast ? lastItemRef : undefined}>
                            <NotificationItem
                                notification={notification}
                                onClick={handleSelect}
                            />
                        </div>
                        
                    );
                })}
                {allNotifications.length === 0 && !isFetching && (
                    <div className={styles.centered}>
                        <p className={styles.empty}>You don't have any notifications yet.</p>
                    </div>
                )}
                {isLoadingMore && (
                    <div className={styles.loadMoreSpinner}>
                        <BounceLoader color="var(--customer-primary)" size={24} />
                    </div>
                )}
            </div>
        );
    };

    return (
        <CustomerPageLayout showBackButton={true} contentContainerClass={styles.pageContent}
        title='Notification'>
            <div className={styles.container}>
                {isInitialLoad && (
                    <div className={styles.loadingOverlay}>
                        <BounceLoader color="var(--customer-primary)" />
                    </div>
                )}

                <div className={styles.listSection}>
                    {/* <div className={styles.pageHeader}>
                        <div className={styles.pageHeaderLeft}>
                            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.6667 5.5L9.16667 11L14.6667 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <h1 className={styles.pageTitle}>Notification</h1>
                        </div>
                        <EkedcIconMark className={styles.logoIcon} />
                    </div> */}

                    <div className={styles.tabBar}>
                        <button
                            className={`${styles.tab} ${activeFilter === NotificationFilter.All ? styles.tabActive : ''}`}
                            onClick={() => handleTabChange(NotificationFilter.All)}
                        >
                            All
                        </button>
                        <button
                            className={`${styles.tab} ${activeFilter === NotificationFilter.Alert ? styles.tabActive : ''}`}
                            onClick={() => handleTabChange(NotificationFilter.Alert)}
                        >
                            Alerts
                        </button>
                        <button
                            className={`${styles.tab} ${activeFilter === NotificationFilter.Update ? styles.tabActive : ''}`}
                            onClick={() => handleTabChange(NotificationFilter.Update)}
                        >
                            Updates
                        </button>
                    </div>

                    {renderListContent()}
                </div>
            </div>
            {refundReference && <RefundTransactionModal reference={refundReference} onClose={() => setRefundReference(null)} />}
        </CustomerPageLayout>
    );
};

export default NotificationPage;
